import React, { createContext, useContext, useEffect, useState } from "react";
import { auth, db } from "../firebase";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  reload,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  sendEmailVerification,
  GoogleAuthProvider,
} from "firebase/auth";
import { ref, set, get, update, remove } from "firebase/database";
import { deleteUser } from "firebase/auth";

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(undefined);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const signingUp = React.useRef(false);
  // ── SIGNUP: Email + password ──
  async function signupDirect(profileData) {
    signingUp.current = true;
    const result = await createUserWithEmailAndPassword(auth, profileData.email, profileData.password);
    const user = result.user;
    await updateProfile(user, { displayName: profileData.name });
    const referralCode = user.uid.slice(0, 8).toUpperCase();
    const emailLower = profileData.email?.toLowerCase() || "";
    const isEduEmail = emailLower.endsWith(".edu") || emailLower.endsWith(".ac.in") || emailLower.endsWith(".edu.in");
    const dbData = profileData.userType === "student" ? {
      name: profileData.name,
      email: profileData.email,
      phone: profileData.phone,
      college: profileData.college,
      userType: "student",
      verified: isEduEmail,
      eduVerified: isEduEmail,
      idVerificationStatus: isEduEmail ? "approved" : "none",
      phoneVerified: false,
      referralCode,
      rating: 0,
      totalRatings: 0,
      collegeChanges: 0,
      createdAt: new Date().toISOString(),
    } : {
      name: profileData.name,
      email: profileData.email,
      phone: profileData.phone,
      childSchool: profileData.childSchool,
      childClass: profileData.childClass,
      city: profileData.city,
      userType: "parent",
      verified: true,
      idVerificationStatus: "approved",
      phoneVerified: false,
      rating: 0,
      totalRatings: 0,
      createdAt: new Date().toISOString(),
    };
    await set(ref(db, `users/${user.uid}`), dbData);
    setUserProfile({ id: user.uid, ...dbData });
    // Send verification email then sign out — user must verify before accessing app
    await sendEmailVerification(user, { url: "https://campusswap.in/login" });
    await signOut(auth);
    setCurrentUser(null);
    setUserProfile(null);
    signingUp.current = false;
    return result;
  }

  async function resendVerificationEmail() {
    if (auth.currentUser && !auth.currentUser.emailVerified) {
      await sendEmailVerification(auth.currentUser, { url: "https://campusswap.in/login" });
    }
  }

  // ── Ban check helper ──
  async function checkBanned(uid) {
    const snap = await get(ref(db, `bannedUsers/${uid}`));
    if (snap.exists()) {
      await signOut(auth);
      throw Object.assign(new Error("Your account has been removed. Contact support if this is a mistake."), { code: "account-banned" });
    }
  }

  // ── LOGIN: Email + Password ──
  async function login(email, password) {
    const result = await signInWithEmailAndPassword(auth, email, password);
    await checkBanned(result.user.uid);
    if (!result.user.emailVerified) {
      await signOut(auth);
      throw Object.assign(new Error("Please verify your email before logging in."), { code: "auth/email-not-verified" });
    }
    return result;
  }

  // ── LOGIN: Google — shared profile-creation logic ──
  async function _handleGoogleResult(result) {
    await checkBanned(result.user.uid);
    const snap = await get(ref(db, `users/${result.user.uid}`));
    const isNewUser = !snap.exists();
    if (isNewUser) {
      signingUp.current = true;
      const referralCode = result.user.uid.slice(0, 8).toUpperCase();
      const emailLower = result.user.email?.toLowerCase() || "";
      const isEduEmail = emailLower.endsWith(".edu") || emailLower.endsWith(".ac.in") || emailLower.endsWith(".edu.in");
      const dbData = {
        name: result.user.displayName || result.user.email?.split("@")[0] || "User",
        email: result.user.email || "",
        photo: result.user.photoURL || "",
        userType: "student",
        verified: isEduEmail,
        eduVerified: isEduEmail,
        idVerificationStatus: isEduEmail ? "approved" : "none",
        phoneVerified: false,
        referralCode,
        rating: 0,
        totalRatings: 0,
        collegeChanges: 0,
        createdAt: new Date().toISOString(),
      };
      await set(ref(db, `users/${result.user.uid}`), dbData);
      setUserProfile({ id: result.user.uid, ...dbData });
      signingUp.current = false;
    }
    return { result, isNewUser };
  }

  // ── LOGIN: Google ──
  async function loginWithGoogle() {
    const provider = new GoogleAuthProvider();
    // TWA / Android WebView / standalone PWA don't support popups — use redirect
    const isMobile = /android|iphone|ipad|ipod/i.test(navigator.userAgent) ||
      window.matchMedia("(display-mode: standalone)").matches;
    if (isMobile) {
      await signInWithRedirect(auth, provider);
      return; // page navigates away; result handled by getRedirectResult on return
    }
    const result = await signInWithPopup(auth, provider);
    return await _handleGoogleResult(result);
  }

  function logout() {
    setUserProfile(null);
    return signOut(auth);
  }

  async function fetchUserProfile(uid) {
    try {
      const snap = await get(ref(db, `users/${uid}`));
      if (snap.exists()) setUserProfile({ id: uid, ...snap.val() });
      else if (!signingUp.current) setUserProfile(null);
    } catch (err) {
      // Don't set null — leave as undefined so PrivateRoute doesn't wrongly redirect
    }
  }

  async function reloadUser() {
    if (auth.currentUser) {
      await reload(auth.currentUser);
      setCurrentUser({ ...auth.currentUser });
    }
  }

  async function updateUserProfile(uid, data) {
    await update(ref(db, `users/${uid}`), data);
    await fetchUserProfile(uid);
  }

  async function deleteAccount(uid) {
    // Delete user-owned data from Firebase
    const paths = [
      `users/${uid}`,
      `saved/${uid}`,
      `notifications/${uid}`,
      `emailOTPs/${uid}`,
    ];
    // Delete user's own listings
    try {
      const listingsSnap = await get(ref(db, "listings"));
      if (listingsSnap.exists()) {
        const entries = Object.entries(listingsSnap.val());
        for (const [lid, l] of entries) {
          if (l.sellerId === uid) paths.push(`listings/${lid}`);
        }
      }
    } catch (e) { /* non-critical */ }
    await Promise.allSettled(paths.map(p => remove(ref(db, p))));
    // Delete Firebase Auth account (requires recent login)
    await deleteUser(auth.currentUser);
    setCurrentUser(null);
    setUserProfile(null);
  }

  // ── Handle Google redirect result on app load (mobile/TWA) ──
  useEffect(() => {
    getRedirectResult(auth).then(async (result) => {
      if (result?.user) {
        await _handleGoogleResult(result);
      }
    }).catch(() => {});
  }, []); // eslint-disable-line

  // Periodic lastActive update (every 5 min) + on window focus
  useEffect(() => {
    if (!currentUser) return;
    const updateActive = () => {
      update(ref(db, `users/${currentUser.uid}`), { lastActive: Date.now() }).catch(() => {});
    };
    const interval = setInterval(updateActive, 5 * 60 * 1000);
    window.addEventListener("focus", updateActive);
    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", updateActive);
    };
  }, [currentUser]); // eslint-disable-line

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Check if this user is banned/deleted by admin
        try {
          const bannedSnap = await get(ref(db, `bannedUsers/${user.uid}`));
          if (bannedSnap.exists()) {
            await signOut(auth);
            setCurrentUser(null);
            setUserProfile(null);
            setLoading(false);
            return;
          }
        } catch (e) { /* network error — allow through, will be caught on next action */ }
        // Check if auto-suspended by community reports
        let hasProfile = false;
        try {
          const userSnap = await get(ref(db, `users/${user.uid}`));
          hasProfile = userSnap.exists();
          if (hasProfile && userSnap.val().suspended) {
            await signOut(auth);
            setCurrentUser(null);
            setUserProfile(null);
            setLoading(false);
            return;
          }
        } catch (e) { /* network error — allow through */ }
        setCurrentUser(user);
        await fetchUserProfile(user.uid);
        try {
          const adminSnap = await get(ref(db, `admins/${user.uid}`));
          setIsAdmin(adminSnap.exists());
        } catch (e) { setIsAdmin(false); }
        // Update last active timestamp — only if profile exists to avoid creating ghost entries
        try {
          if (hasProfile) {
            await update(ref(db, `users/${user.uid}`), { lastActive: Date.now() });
          }
        } catch (e) { /* non-critical */ }
      } else {
        setCurrentUser(null);
        setUserProfile(null);
        setIsAdmin(false);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AuthContext.Provider value={{
      currentUser, userProfile, isAdmin,
      login, loginWithGoogle, logout,
      signupDirect, resendVerificationEmail,
      fetchUserProfile, updateUserProfile, deleteAccount,
      reloadUser,
    }}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
